import { create } from 'zustand'
import useFiltersStore from './filtersStore'
import { trackSelectTicket } from '../utilities/tracking'
export const SEAT_VIEW_PLACEHOLDER =
  'https://blocktickets-assets.nyc3.cdn.digitaloceanspaces.com/seatview-placeholder.png'

/** Per-ticket face value for cart totals; complimentary offers count as $0 */
const effectiveUnitPrice = (ticketGroup) => {
  const offer =
    ticketGroup?.offer || ticketGroup?.package || ticketGroup?.listing?.offer
  if (offer?.freeOffer) return 0
  return ticketGroup.price ?? 0
}

// For resetting state
const resetState = {
  scale: 1,
  scaleThreshold: 4,
  arePathsHidden: false,
  selectedFromMap: [],
}

const useSeatmapStore = create((set, get) => ({
  // State
  data: null,
  background: null,
  scale: 1,
  scaleThreshold: 4, // when sections become visible
  arePathsHidden: false,
  seatedError: null,
  seatBorderRadius: 33,
  maxScale: 20,
  seatmapId: null,
  // lookups into ticketGroups
  seatLookupTable: {},
  seatOffersLookupTable: {},
  sectionLookupTable: {},
  selectedFromMap: [],
  totalCount: 0,
  totalPrice: 0,
  bucket: `https://blocktickets.nyc3.cdn.digitaloceanspaces.com`,

  // Setters
  setData: (data) => set({ data: data }),
  setBackground: (background) => set({ background }),
  setScale: (scale) => set({ scale }),
  setScaleThreshold: (scaleThreshold) => set({ scaleThreshold }),
  setArePathsHidden: (hidden) => set({ arePathsHidden: hidden }),
  setSeatLookupTable: (seatLookupTable) => set({ seatLookupTable }),
  setSeatOffersLookupTable: (seatOffersLookupTable) =>
    set({ seatOffersLookupTable }),
  setSectionLookupTable: (sectionLookupTable) => set({ sectionLookupTable }),
  setSelectedFromMap: (selectedFromMap) => set({ selectedFromMap }),
  setSeatedError: (seatedError) => set({ seatedError }),
  setSeatBorderRadius: (seatBorderRadius) => set({ seatBorderRadius }),
  setMaxScale: (maxScale) => set({ maxScale }),
  setSeatmapId: (seatmapId) => set({ seatmapId }),

  // Helper functions
  _addToSeats: (ticketGroup) => {
    set((state) => ({
      selectedFromMap: [...state.selectedFromMap, ticketGroup],
    }))

    get()._calculateTotals()
  },

  _calculateTotals: () => {
    const { selectedFromMap } = get()
    const newTotalPrice = selectedFromMap
      .map((ticketGroup) => {
        const unit = effectiveUnitPrice(ticketGroup)
        if (ticketGroup.GA) {
          return ticketGroup.quantity * unit
        } else {
          return unit
        }
      })
      .reduce((sum, price) => sum + price, 0)
    const seatedCount = selectedFromMap
      .filter((ticketGroup) => ticketGroup.GA === false)
      .map((ticketGroup) => ticketGroup.seatId).length
    const gaCount = selectedFromMap
      .filter((ticketGroup) => ticketGroup.GA === true)
      .reduce((sum, ticketGroup) => sum + ticketGroup.quantity, 0)

    const newTotalCount = seatedCount + gaCount

    set((state) => ({
      totalCount: newTotalCount,
      totalPrice: newTotalPrice,
    }))
  },

  _withingEventTicketLimit: (additionalTickets) => {
    const { selectedFromMap, eventTicketLimit } = get()
    if (!eventTicketLimit) return true

    const currentGATickets = selectedFromMap
      .filter((ticketGroup) => ticketGroup.GA === true)
      .reduce((sum, ticketGroup) => sum + ticketGroup.quantity, 0)
    const currentSpecificSeats = selectedFromMap
      .filter((ticketGroup) => ticketGroup.GA === false)
      .map((ticketGroup) => ticketGroup.seatId).length
    const totalCurrentTickets = currentGATickets + currentSpecificSeats

    // Checks adding x tickets wont go above eventTicketLimit...
    return totalCurrentTickets + additionalTickets <= eventTicketLimit
  },

  // 'Public' functions
  resetMapState: () => {
    set(resetState)
  },

  selectGASeats: (selectedGroups) => {
    console.log('selectGASeats: ', selectedGroups)
    const totalNewTickets = selectedGroups.reduce(
      (sum, { quantity }) => sum + quantity,
      0
    )

    if (!get()._withingEventTicketLimit(totalNewTickets)) {
      // Error out etc
      const { eventTicketLimit } = get()
      set((state) => ({
        seatedError: {
          title: 'Max ticket limit reached',
          message: `Adding these tickets would exceed the ticket limit of ${eventTicketLimit}. Please change your selection.`,
          buttonText: 'Close',
        },
      }))
      return
    }

    const organization = useFiltersStore.getState().event?.organization
    selectedGroups.forEach((group) => {
      get()._addToSeats(group)
      trackSelectTicket({
        organization,
        ticket: group,
        quantity: group?.quantity || 1,
      })
    })
  },

  selectSeatedOffers: (seatId, selectedGroups) => {
    const totalNewTickets = selectedGroups.reduce(
      (sum, { quantity }) => sum + quantity,
      0
    )

    if (!get()._withingEventTicketLimit(totalNewTickets)) {
      const { eventTicketLimit } = get()
      set((state) => ({
        seatedError: {
          title: 'Max ticket limit reached',
          message: `Adding these tickets would exceed the ticket limit of ${eventTicketLimit}. Please change your selection.`,
          buttonText: 'Close',
        },
      }))
      return
    }

    set((state) => ({
      data: {
        ...state.data,
        seats: {
          ...state.data.seats,
          [seatId]: { ...state.data.seats[seatId], selected: true },
        },
      },
    }))

    const organization = useFiltersStore.getState().event?.organization
    const seatNumber = get().data.seats[seatId].seatNumber

    selectedGroups.forEach((group) => {
      const { quantity, ...ticketGroup } = group
      const selectedOffer = ticketGroup?.offer || ticketGroup?.package
      const selectedTicket = {
        seatId,
        seatNumber,
        ...ticketGroup,
        offer: selectedOffer,
        offerIds: selectedOffer?.id ? [selectedOffer.id] : ticketGroup.offerIds,
      }

      get()._addToSeats(selectedTicket)
      trackSelectTicket({
        organization,
        ticket: selectedTicket,
        quantity: quantity || 1,
      })
    })
  },

  selectSpecificSeat: (id, ticketGroup) => {
    console.log('selectSpecific TG: ', ticketGroup)
    if (!get()._withingEventTicketLimit(1)) {
      const { eventTicketLimit } = get()
      set((state) => ({
        seatedError: {
          title: 'Max ticket limit reached',
          message: `Adding these tickets would exceed the ticket limit of ${eventTicketLimit}. Please change your selection.`,
          buttonText: 'Close',
        },
      }))
      return
    }

    set((state) => ({
      data: {
        ...state.data,
        seats: {
          ...state.data.seats,
          [id]: { ...state.data.seats[id], selected: true },
        },
      },
    }))

    const selectedOffer = ticketGroup?.offer || ticketGroup?.package
    const selectedTicket = {
      seatId: id,
      seatNumber: get().data.seats[id].seatNumber,
      ...ticketGroup,
      offer: selectedOffer,
      offerIds: selectedOffer?.id ? [selectedOffer.id] : ticketGroup.offerIds,
    }
    get()._addToSeats(selectedTicket)
    trackSelectTicket({
      organization: useFiltersStore.getState().event?.organization,
      ticket: selectedTicket,
      quantity: 1,
    })
  },

  unselectSeat: (seatId, ticketGroup) => {
    if (ticketGroup.GA) {
      set((state) => ({
        selectedFromMap: state.selectedFromMap
          .map((group) => {
            if (
              group.sectionId === ticketGroup.sectionId &&
              group.id === ticketGroup.id
            ) {
              return {
                ...group,
                quantity: group.quantity - 1,
              }
            }
            return group
          })
          .filter((group) => !group.GA || (group.GA && group.quantity > 0)),
      }))
    } else {
      set((state) => ({
        selectedFromMap: state.selectedFromMap.filter(
          (group) => group.seatId !== seatId
        ),
        data: {
          ...state.data,
          seats: {
            ...state.data.seats,
            [seatId]: { ...state.data.seats[seatId], selected: false },
          },
        },
      }))
    }

    get()._calculateTotals()
  },

  resetMapSelection: () => {
    set((state) => {
      const allSelectedSeatIds = state.selectedFromMap
        .filter((ticketGroup) => ticketGroup?.seatId !== undefined)
        .map((ticketGroup) => ticketGroup.seatId)

      // Safety check for undefined state.data
      if (!state.data) return { selectedFromMap: [] }

      const { seats } = state.data

      const updatedSeats = {
        ...seats,
        ...Object.fromEntries(
          allSelectedSeatIds.map((seatId) => [
            seatId,
            { ...seats[seatId], selected: false },
          ])
        ),
      }

      return {
        selectedFromMap: [],
        totalCount: 0,
        totalPrice: 0,
        data: {
          ...state.data,
          seats: updatedSeats,
        },
      }
    })
  },

  getTicketImage: (venue, sectionNumber, type = 'highlights') => {
    const { bucket } = get()
    let imgURL = `${bucket}/venues/${venue}/${type}/${sectionNumber}.png`
    return imgURL // sectionNumber
  },
}))

export default useSeatmapStore
